import { Injectable } from "@angular/core";
import {
  PRIMITIVE,
  ServiceMessageBrokerFactory,
  ServiceMessageBroker,
} from "shared/provider-webworker";

import {
  LimitClientTradeLookupService,
  AlgoClientTradeLookupService,
  BMClientTradeLookupService,
  TradeDealLookupService,
} from "./TradeDealLookupService";
import { PrimaryBlotterLookupService } from "../../impl/PrimaryBlotterLookupService";
import { ModelUpdate } from "../../impl/ModelUpdate";
import { ITradeDealPromiseLookupService } from "../ITradeDealPromiseLookupService";
import { DealPrivatizationService } from "../../../model/privatization/DealPrivatizationService";

import { CubiqWorker } from "../../../worker/CubiqWorker";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { RawDataDealAdapter } from "../../../mediator/adapter/RawDataDealAdapter";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { NotificationService } from "../../notification/impl/NotificationService";
import { TraderGroupPrimaryLookupService } from "./TraderGroupPrimaryLookupService";

@Injectable()
export abstract class TradeDealPrimaryLookupService
  extends PrimaryBlotterLookupService<string, CubiqTS.DealImpl>
  implements ITradeDealPromiseLookupService
{
  tradeDataObservable: any;
  protected abstract blotterProfile: string[];
  protected blotterType: string | symbol;
  protected externalTraderGroups: Set<string> = new Set();

  constructor(
    protected base: TradeDealLookupService,
    factory: ServiceMessageBrokerFactory,
    cubiqWorker: CubiqWorker,
    dataAdapter: RawDataDealAdapter,
    notificationService: NotificationService,
    protected privatizationService: DealPrivatizationService,
    protected traderGroupLookupService: TraderGroupPrimaryLookupService,
  ) {
    super(
      base,
      factory,
      CubiqTS.DealImpl,
      cubiqWorker,
      "DealImpl",
      CubiqTS.DealImpl,
      dataAdapter,
      notificationService,
    );

    let tgExternallyFilledPromise = new Promise<boolean>((res) => {
      this.traderGroupLookupService.isPrimed().then((isPrimed) => {
        if (!isPrimed) return res(isPrimed);

        this.traderGroupLookupService.getAll().then(
          (traderGroups) => {
            traderGroups.forEach((traderGroup) => {
              if (traderGroup.isExternallyFilledFg) {
                let id = traderGroup?.groupId?.toString();
                if (id != null && typeof id === "string") this.externalTraderGroups.add(id);
              }
            });
            res(true);
          },
          () => res(false),
        );
      });
    });

    let dependencyPromise: Promise<Array<any>> = Promise.all([
      this.privatizationService.isReady(),
      tgExternallyFilledPromise,
    ]);

    cubiqWorker.isAuth(`${this.constructor.name}`).then(() => {
      this.tradeDataObservable = cubiqWorker
        .primeDataProfile(this.blotterProfile, this.blotterType)
        .subscribe({
          next: (deal: CubiqTS.DealImpl[]) => {
            dependencyPromise.then((_: Array<any>) => {
              deal = deal
                .filter((o) => o != null && this.isPrimeEligible(o))
                .map((o) => this.privatizationService.checkPrivatization(o));
              this.base.prime(deal, false);
            });
          },
          error: (e: Error) => {
            logger.out.error("Failed to prime trade data: " + e);
            this._primed.reject(e);
          },
          complete: () => {
            dependencyPromise.then(
              () => {
                this.primePostProcess();
                this.base.prime([], true);
                this._primed.resolve(true);
              },
              (err) => {
                this._primed.reject(err);
              },
            );
          },
        });
    });

    this.cubiqWorker
      .getEncodableObservable<CubiqTS.DealImpl>(CubiqTS.DealImpl.name)
      .subscribe((deal: CubiqTS.DealImpl) => {
        dependencyPromise.then(() => {
          try {
            this.processDeal(deal);
          } catch (e) {
            logger.out.error("Error processing trade data event: " + e);
          }
        });
      });
  }

  protected processDeal(deal: CubiqTS.DealImpl): void {
    if (
      deal.dealStateId !== undefined &&
      deal.dealStateId !== null &&
      deal.dealStateId !== CubiqTS.OrderDealState.CANCELLED &&
      deal.dealStateId !== CubiqTS.OrderDealState.FILL_CANCEL
    ) {
      let existingDeal = this.base.getItem(this.base.getItemKey(deal));

      // When a deal update is issued for a deal the benchmark order type might not be populated
      if (
        deal.benchmarkOrderType == null &&
        existingDeal != null &&
        existingDeal.benchmarkOrderType != null
      ) {
        deal.benchmarkOrderType = existingDeal.benchmarkOrderType;
      }

      if (
        existingDeal &&
        existingDeal.dealStateId !== CubiqTS.OrderDealState.ACTIVE &&
        existingDeal.dealStateId !== CubiqTS.OrderDealState.FILL_PENDING &&
        deal.dealStateId === CubiqTS.OrderDealState.ACTIVE
      ) {
        // Deals can arrive out of sequence (https://www.bcdfx.com/ijira/browse/OMS-20851) so ignore any
        // ACTIVE deals only when existing order is not ACTIVE or FILL_PENDING
        return;
      } else if (this.isPrimeEligible(deal) && this.filterEvent(deal)) {
        deal = this.privatizationService.checkPrivatization(deal);
        this.base.mapItem(deal);
        this.fireModelUpdateEvent(deal);
      }
    } else {
      deal = this.privatizationService.checkPrivatization(deal);
      this.fireModelDeleteEvent(deal.id.toString());
    }
  }

  protected abstract filterEvent(deal: CubiqTS.ConcreteEncodable): boolean;

  protected keyProperty(): string {
    return "id";
  }

  protected createModelUpdateEvent(updatedItem: CubiqTS.DealImpl): any {
    return updatedItem;
  }

  @ModelUpdate(CubiqTS.ClientImpl)
  protected onClientUpdate(client: CubiqTS.ClientImpl, deal: CubiqTS.DealImpl): boolean {
    return deal.clientCode == client.clientId;
  }

  @ModelUpdate(CubiqTS.AccountImpl)
  protected onAccountUpdate(account: CubiqTS.AccountImpl, deal: CubiqTS.DealImpl): boolean {
    return deal.accountCode == account.id;
  }

  @ModelUpdate(CubiqTS.CurrencyImpl)
  protected onCurrencyUpdate(currency: CubiqTS.CurrencyImpl, deal: CubiqTS.DealImpl): boolean {
    return deal.productId.startsWith(currency.id) || deal.productId.endsWith(currency.id);
  }

  @ModelUpdate(CubiqTS.CurrencyPairImpl)
  protected onCurrencyPairUpdate(
    currencyPair: CubiqTS.CurrencyPairImpl,
    deal: CubiqTS.DealImpl,
  ): boolean {
    let pair: string = currencyPair.base + currencyPair.nonBase,
      inverse: string = currencyPair.nonBase + currencyPair.base;
    return deal.productId == pair || deal.productId == inverse;
  }

  @ModelUpdate(CubiqTS.TradingGroupImpl)
  protected onTraderGroupUpdate(
    traderGroup: CubiqTS.TradingGroupImpl,
    deal: CubiqTS.DealImpl,
  ): boolean {
    if (traderGroup.isExternallyFilledFg) {
      let id = traderGroup?.groupId?.toString();
      if (id != null && typeof id === "string") this.externalTraderGroups.add(id);
      else this.externalTraderGroups.delete(id);
    }

    return this.compareIds(deal.traderGroupId, traderGroup.groupId);
  }

  @ModelUpdate(CubiqTS.SalesGroupImpl)
  protected onSalesGroupUpdate(
    salesGroup: CubiqTS.SalesGroupImpl,
    deal: CubiqTS.DealImpl,
  ): boolean {
    return this.compareIds(deal.salesGroupId, salesGroup.groupId);
  }

  @ModelUpdate(CubiqTS.UserImpl)
  protected onUserUpdate(user: CubiqTS.UserImpl, deal: CubiqTS.DealImpl): boolean {
    return (
      deal.entryUserCode == user.userCode ||
      deal.fillUserCode == user.userCode ||
      this.compareIds(deal.confirmedSalesUserId, user.userId)
    );
  }

  getTradeDeal(id: string): Promise<CubiqTS.DealImpl> {
    return new Promise<CubiqTS.DealImpl>((res, rej) => {
      res(this.base.getTradeDeal(id));
    });
  }

  getAllTradeDeals(): Promise<Array<CubiqTS.DealImpl>> {
    return new Promise<Array<CubiqTS.DealImpl>>((res, rej) => {
      res(this.base.getAllTradeDeals());
    });
  }

  public isPrimeEligible(deal: CubiqTS.DealImpl): boolean {
    return true;
  }

  public primePostProcess(): void {
    try {
      this.base.getAllSpotReservationDealIds().forEach((dealId) => {
        this.base.deleteItemById(dealId);
      });
    } catch (e) {
      logger.out.error("Error post processing deals" + e);
    }
  }

  protected isClientTrade(deal: CubiqTS.DealImpl): boolean {
    return (
      deal.spotReservationFg != true &&
      (deal.makerDealRef === null || deal.aggregateId == null || deal.aggregateId.isZero())
    );
  }
}

@Injectable()
export class LimitClientTradePrimaryLookupService extends TradeDealPrimaryLookupService {
  protected blotterProfile: string[] = ["Client Trade Blotter Limit"];
  protected blotterType: string | symbol = CubiqTS.DealImpl.name;

  constructor(
    base: LimitClientTradeLookupService,
    factory: ServiceMessageBrokerFactory,
    cubiqWorker: CubiqWorker,
    dataAdapter: RawDataDealAdapter,
    notificationService: NotificationService,
    privatizationService: DealPrivatizationService,
    traderGroupLookupService: TraderGroupPrimaryLookupService,
  ) {
    super(
      base,
      factory,
      cubiqWorker,
      dataAdapter,
      notificationService,
      privatizationService,
      traderGroupLookupService,
    );
  }

  protected filterEvent(event: CubiqTS.ConcreteEncodable): boolean {
    return (
      event instanceof CubiqTS.DealImpl &&
      event.encodableType === "com.bcdfx.model.DealImpl" &&
      event.dealStateId !== CubiqTS.OrderDealState.CANCELLED &&
      event.dealStateId !== CubiqTS.OrderDealState.FILL_CANCEL &&
      (event.orderType === CubiqTS.OrderType.LIMIT_TP ||
        event.orderType === CubiqTS.OrderType.LIMIT_SL ||
        event.orderType === CubiqTS.OrderType.LIMIT_CALL ||
        event.orderType === CubiqTS.OrderType.MARKET) &&
      (event.productType === "SPT" || event.productType === "FWD" || event.productType === "NDF") &&
      (event.strategyType === CubiqTS.StrategyType.STANDARD ||
        event.strategyType === CubiqTS.StrategyType.GAMMA) &&
      this.isClientTrade(event as CubiqTS.DealImpl)
    );
  }

  public isPrimeEligible(deal: CubiqTS.DealImpl): boolean {
    return true;
  }
}

@Injectable()
export class AlgoClientTradePrimaryLookupService extends TradeDealPrimaryLookupService {
  protected blotterProfile: string[] = ["Client Trade Blotter Algo"];
  protected blotterType: string | symbol = CubiqTS.DealImpl.name;

  constructor(
    base: AlgoClientTradeLookupService,
    factory: ServiceMessageBrokerFactory,
    cubiqWorker: CubiqWorker,
    dataAdapter: RawDataDealAdapter,
    notificationService: NotificationService,
    privatizationService: DealPrivatizationService,
    traderGroupLookupService: TraderGroupPrimaryLookupService,
  ) {
    super(
      base,
      factory,
      cubiqWorker,
      dataAdapter,
      notificationService,
      privatizationService,
      traderGroupLookupService,
    );
  }

  protected filterEvent(event: CubiqTS.ConcreteEncodable): boolean {
    return (
      event instanceof CubiqTS.DealImpl &&
      event.encodableType === "com.bcdfx.model.DealImpl" &&
      event.dealStateId !== CubiqTS.OrderDealState.CANCELLED &&
      event.dealStateId !== CubiqTS.OrderDealState.FILL_CANCEL &&
      event.orderType === CubiqTS.OrderType.ALGO &&
      (event.productType === "SPT" || event.productType === "FWD" || event.productType === "NDF") &&
      (event.strategyType === CubiqTS.StrategyType.STANDARD ||
        event.strategyType === CubiqTS.StrategyType.GAMMA) &&
      this.isClientTrade(event as CubiqTS.DealImpl)
    );
  }

  public isPrimeEligible(deal: CubiqTS.DealImpl): boolean {
    return true;
  }
}

@Injectable()
export class BMClientTradePrimaryLookupService extends TradeDealPrimaryLookupService {
  protected blotterProfile: string[] = ["Client Trade Blotter BM"];
  protected blotterType: string | symbol = CubiqTS.DealImpl.name;

  constructor(
    base: BMClientTradeLookupService,
    factory: ServiceMessageBrokerFactory,
    cubiqWorker: CubiqWorker,
    dataAdapter: RawDataDealAdapter,
    notificationService: NotificationService,
    privatizationService: DealPrivatizationService,
    traderGroupLookupService: TraderGroupPrimaryLookupService,
  ) {
    super(
      base,
      factory,
      cubiqWorker,
      dataAdapter,
      notificationService,
      privatizationService,
      traderGroupLookupService,
    );
  }

  protected filterEvent(event: CubiqTS.ConcreteEncodable): boolean {
    return (
      event instanceof CubiqTS.DealImpl &&
      event.encodableType === "com.bcdfx.model.DealImpl" &&
      event.dealStateId !== CubiqTS.OrderDealState.CANCELLED &&
      event.dealStateId !== CubiqTS.OrderDealState.FILL_CANCEL &&
      event.orderType === CubiqTS.OrderType.BENCHMARK &&
      (event as CubiqTS.DealImpl).benchmarkOrderType === CubiqTS.BenchmarkOrderType.Standard &&
      this.isClientTrade(event as CubiqTS.Deal)
    );
  }
}
